import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Calendar, CheckCircle2, Clock, Download, Image as ImageIcon, Eye } from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";
import { Brand } from "../../context/CompanyContext";

export default function ClientContracts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/client/contracts");
        setItems(data.items || []);
      } catch (e) {
        toast.error(formatApiError(e, "Erro ao carregar"));
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="client-contracts-page">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl text-[#fdfbf7]">
          Os meus <span className="italic text-[#d4af37]">Contratos</span>
        </h1>
        <p className="mt-1 text-sm text-[#a0b2af]">
          Histórico dos seus contratos com a <Brand />.
        </p>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[0, 1].map(i => <div key={i} className="h-24 rounded-xl border border-[#1a4640] bg-[#062c27] animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#1a4640] bg-[#062c27] py-16 text-center">
          <FileText size={32} className="mx-auto text-[#1a4640]" />
          <p className="mt-4 text-sm text-[#a0b2af]">Ainda não tem contratos. Aparecerão aqui quando o admin os enviar.</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map(c => (
            <li key={c.id} data-testid={`client-contract-row-${c.id}`}
                className="rounded-xl border border-[#1a4640] bg-[#062c27] p-5 hover:border-[#d4af37]/50 transition-colors">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {c.status === "signed" ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-emerald-500/10 text-emerald-300 border border-emerald-500/40">
                        <CheckCircle2 size={11} /> Assinado
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-[#3a2f0a] text-[#e8c860] border border-[#d4af37]/40">
                        <Clock size={11} /> Pendente
                      </span>
                    )}
                    {c.image_authorization && (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${
                        c.image_authorization === "yes"
                          ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/40"
                          : "bg-red-500/10 text-red-300 border-red-500/40"
                      }`}>
                        <ImageIcon size={10} /> {c.image_authorization === "yes" ? "Autoriza imagens" : "Não autoriza"}
                      </span>
                    )}
                  </div>
                  <h3 className="mt-2 font-display text-base text-[#fdfbf7]">
                    Contrato · {(c.id || "").slice(0, 8).toUpperCase()}
                  </h3>
                  <div className="mt-1 text-xs text-[#a0b2af] flex items-center gap-3 flex-wrap">
                    <span><Calendar size={11} className="inline mr-1" />Criado: {new Date(c.created_at).toLocaleDateString("pt-PT")}</span>
                    {c.signed_at && <span>Assinado: {new Date(c.signed_at).toLocaleDateString("pt-PT")}</span>}
                    <span>{(c.properties || []).filter(p => p.included).length} propriedades · {c.global_scope ? "global" : "específicas"}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {c.status === "sent" && c.token && (
                    <a href={`/contratos/assinar/${c.token}`}
                       className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[11px] font-semibold uppercase tracking-wider bg-[#d4af37] text-[#041f1b] hover:bg-[#e8c860] transition-all">
                      Assinar agora
                    </a>
                  )}
                  {c.status === "signed" && (
                    <>
                      <Link to={`/cliente/contratos/${c.id}`}
                            data-testid={`client-contract-view-${c.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[11px] font-semibold uppercase tracking-wider bg-[#d4af37] text-[#041f1b] hover:bg-[#e8c860] transition-all">
                        <Eye size={11} /> Ver contrato
                      </Link>
                      <a href={`${BACKEND_URL}/api/client/contracts/${c.id}/pdf`}
                         target="_blank" rel="noopener noreferrer"
                         data-testid={`client-contract-pdf-${c.id}`}
                         className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[11px] font-semibold uppercase tracking-wider border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10 transition-all">
                        <Download size={11} /> PDF
                      </a>
                    </>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
